import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";

import PreviewTab from "containers/admin/clients/touchpoint/tab-components/preview-tab/preview-tab";
import DetailsTab from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab";
import VersionHistoryTab from "containers/admin/clients/touchpoint/tab-components/version-history-tab/version-history-tab";

import { TouchpointPageHeader } from "containers/admin/clients/touchpoint/components/touchpoint-page-header/touchpoint-page-header";
import { GeneralCommentsFAB } from "components/partials/general-comments-fab/general-comments-fab";

import { CommentContextProvider } from "contexts/comment-context";

import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

import { hasPermission } from "utilities/user";
import { getIsCommentingDisabled } from "utilities/touchpoint";
import { usePagePermission } from "hooks/use-page-permission";
import { useTouchpointOperations } from "containers/admin/clients/touchpoint/hooks/use-touchpoint-operations";
import { useTouchpointVersions } from "hooks/api/use-touchpoint-versions";

import { TOUCHPOINT_PREVIEW_VERSION_ID_QUERY } from "utilities/app-routes";

import { Permission } from "types/auth";
import { TouchpointLockingProvider } from "contexts/touchpoint-locking-context";
import PageContent from "components/partials/page-content/page-content";
import { BuilderRoutes } from "containers/admin/clients/touchpoint/components/builder/builder-routes/builder-routes";
import { useIsEmailWriterGenerating } from "containers/admin/clients/touchpoint/hooks/use-is-email-writer-generating";

/** Preserves `location.state` (e.g. breadcrumb trail from inbox / batch) when redirecting to preview. */
const TouchpointPreviewIndexRedirect = () => {
  const { state } = useLocation();
  return <Navigate to="preview" replace state={state} />;
};

const VersionHistoryTabRoute = ({
  setSelectedVersion,
}: {
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
}) => {
  const isEmailWriterGenerating = useIsEmailWriterGenerating();
  const location = useLocation();

  if (isEmailWriterGenerating) {
    return <Navigate to="../preview" replace state={location.state} />;
  }

  return <VersionHistoryTab setSelectedVersion={setSelectedVersion} />;
};

const TouchpointPage = () => {
  const [selectedVersion, setSelectedVersion] = useState<TouchpointVersionAttributes>();
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  const prevTouchpointIdRef = useRef<string | undefined>(undefined);

  const currentTouchpoint = useCurrentTouchpoint();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentUser = useCurrentUser();

  const {
    data: touchpointVersions,
    isLoading: isVersionHistoryLoading,
    isFetching: isVersionsFetching,
  } = useTouchpointVersions({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const stripVersionIdFromUrl = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (!next.has(TOUCHPOINT_PREVIEW_VERSION_ID_QUERY)) {
      return;
    }
    next.delete(TOUCHPOINT_PREVIEW_VERSION_ID_QUERY);
    // Preserve router state (breadcrumbs / referrer) — a plain `setSearchParams` navigation drops it.
    setSearchParams(next, { replace: true, state: location.state });
  }, [searchParams, setSearchParams, location.state]);

  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  usePagePermission(Permission.PERM_TOUCHPOINT_READ);

  const {
    loadTouchpointComments,
    loadCommentsSummary,
    submitNewTouchpointComment,
    updateTouchpointComment,
    loadTouchpointCommentReplies,
    submitNewTouchpointCommentReply,
  } = useTouchpointOperations(selectedVersion?.id, isClientUser);

  // After version history loads, default selection to the latest version the user can see —
  // for clients, that's the latest VISIBLE_TO_CLIENT version of the touchpoint.
  // Optional `?versionId=` opens that version when present and valid; otherwise
  // keep current behavior (first in the loaded list).
  const versionItems = useMemo(() => touchpointVersions?.items ?? [], [touchpointVersions?.items]);

  // Resolve `?versionId=` whenever list data updates — do not bail just because something else set
  // `selectedVersion` first (e.g. PreviewTabHeader refetch racing with the URL).
  useEffect(() => {
    if (isVersionHistoryLoading || !versionItems.length) {
      return;
    }

    const fromUrl = searchParams.get(TOUCHPOINT_PREVIEW_VERSION_ID_QUERY);

    if (fromUrl) {
      const match = versionItems.find((v) => v.id === fromUrl);
      if (match) {
        if (selectedVersion?.id !== match.id) {
          setSelectedVersion(match);
        }
        return;
      }
      // Deep link id not in the current list snapshot — wait for React Query refetch unless settled
      if (isVersionsFetching) {
        return;
      }
      if (selectedVersion?.id !== versionItems[0]?.id) {
        setSelectedVersion(versionItems[0]);
      }
      return;
    }

    if (!selectedVersion && !isVersionsFetching) {
      setSelectedVersion(versionItems[0]);
    }
  }, [
    isVersionHistoryLoading,
    isVersionsFetching,
    searchParams,
    versionItems,
    selectedVersion?.id,
  ]);

  // After deep link matches selection, strip `versionId` from the address bar (keep `location.state`).
  useEffect(() => {
    const fromUrl = searchParams.get(TOUCHPOINT_PREVIEW_VERSION_ID_QUERY);
    if (!fromUrl || !selectedVersion || selectedVersion.id !== fromUrl) {
      return;
    }
    stripVersionIdFromUrl();
  }, [searchParams, selectedVersion, stripVersionIdFromUrl]);

  const commentingDisabled = getIsCommentingDisabled(isClientUser, selectedVersion?.status);

  useEffect(() => {
    // Only reset selectedVersion when navigating between *different* touchpoints.
    // Skipping on initial mount prevents the reset from racing with the effect
    // that restores the cached version, which would leave the preview blank after
    // back-navigation when React Query returns the same object reference.
    if (
      prevTouchpointIdRef.current !== undefined &&
      prevTouchpointIdRef.current !== currentTouchpoint.id
    ) {
      setSelectedVersion(undefined);
    }
    prevTouchpointIdRef.current = currentTouchpoint.id;
  }, [currentTouchpoint.id]);

  const loadTouchpointCommentsSummary = () => {
    setIsSummaryLoading(true);
    return loadCommentsSummary().finally(() => {
      setIsSummaryLoading(false);
    });
  };

  return (
    <TouchpointLockingProvider>
      <Routes>
        <Route
          path="*"
          element={
            <CommentContextProvider
              loadComments={loadTouchpointComments}
              loadCommentsSummary={loadTouchpointCommentsSummary}
              commentScope={{
                clientId: currentClient.id,
                campaignId: currentCampaign.id,
                touchpointId: currentTouchpoint.id,
              }}
              submitNewComment={submitNewTouchpointComment}
              updateComment={updateTouchpointComment}
              loadCommentReplies={loadTouchpointCommentReplies}
              submitNewCommentReply={submitNewTouchpointCommentReply}
              commentingDisabled={commentingDisabled}>
              <Routes>
                {/* Builder Routes - handle separately without main page layout */}
                {!isClientUser && (
                  <Route
                    path="versions/:versionId/*"
                    element={
                      <>
                        <BuilderRoutes />
                        <GeneralCommentsFAB />
                      </>
                    }
                  />
                )}

                {/* Main Page Layout (Header + Content Wrapper) */}
                <Route
                  path="*"
                  element={
                    <>
                      <TouchpointPageHeader
                        touchpointBatchId={selectedVersion?.touchpointBatchId}
                        selectedVersionFiscalYear={selectedVersion?.fiscalYear}
                      />
                      <PageContent>
                        {!isClientUser ? (
                          <Routes>
                            <Route index element={<TouchpointPreviewIndexRedirect />} />
                            <Route
                              path="preview"
                              element={
                                <PreviewTab
                                  key={currentTouchpoint.id}
                                  isSummaryLoading={isSummaryLoading}
                                  isVersionHistoryLoading={isVersionHistoryLoading}
                                  selectedVersion={selectedVersion}
                                  setSelectedVersion={setSelectedVersion}
                                />
                              }
                            />
                            <Route
                              path="details"
                              element={<DetailsTab selectedVersion={selectedVersion} />}
                            />
                            <Route
                              path="versions"
                              element={
                                <VersionHistoryTabRoute setSelectedVersion={setSelectedVersion} />
                              }
                            />
                            {/* Redirect for old edit route */}
                            <Route path="edit" element={<Navigate to="../details" replace />} />
                          </Routes>
                        ) : (
                          <>
                            <PreviewTab
                              key={currentTouchpoint.id}
                              isVersionHistoryLoading={isVersionHistoryLoading}
                              selectedVersion={selectedVersion}
                              setSelectedVersion={setSelectedVersion}
                            />
                            <GeneralCommentsFAB />
                          </>
                        )}
                      </PageContent>
                    </>
                  }
                />
              </Routes>
            </CommentContextProvider>
          }
        />
      </Routes>
    </TouchpointLockingProvider>
  );
};

export default TouchpointPage;
